/**
 * A wine's saved "Note en ligne" (owner requests, 2026-10-03; cellar schema version 4): one rating
 * on the app's own scale (1 to 5 in half points), calibrated from the ratings found on wine
 * marketplaces and review sites, with the month of the lookup. Kept apart from the person's own
 * tastings, and no longer shown once the wine has a rating of the person's own. Since schema
 * version 5 it also keeps the day of the lookup and each site's own rating, shown in the wine's
 * notes ("03/10/26 : Vivino 4,3 / 5").
 */
import type { CalendarDay } from './drink-by';
import type { MonthValue } from './months';
import { isFutureMonth } from './months';
import { isValidRating, latestRatedTasting, type RatedTasting } from './rating';

export interface SiteScore {
  score: number;
  /** 5, 10, 20 or 100. */
  scale: number;
}

/** One site's rating on its own scale, as found ("Vivino 4,3 / 5"). */
export interface SiteRating extends SiteScore {
  site: string;
}

/** The scales a site's rating may use. */
export const SITE_SCALES: readonly number[] = [5, 10, 20, 100];
/** At most this many sites per lookup. */
export const MAX_SITES = 5;

export interface SavedOnlineRating {
  /** The month of the lookup. */
  checkedOn: MonthValue;
  /** 1 to 5 in half points, like a tasting's rating. */
  rating: number;
  /**
   * The day of the lookup, for the wine's notes (schema version 5; the owner asked for the day
   * there, 2026-10-03, unlike the app's other dates, which are months).
   */
  checkedDay?: CalendarDay;
  /** Each site's own rating (schema version 5), for the wine's notes. */
  sites?: SiteRating[];
}

/** One line of the wine's notes: a site's rating on the day of the lookup. */
export interface OnlineRatingNote extends SiteRating {
  day: CalendarDay;
}

/** Thrown for an online rating that cannot be saved. */
export class InvalidOnlineRatingError extends Error {
  constructor(message = 'Note en ligne invalide.') {
    super(message);
    this.name = 'InvalidOnlineRatingError';
  }
}

/**
 * A site's score on a 0–5 scale. Scores out of 5, 10 and 20 are scaled as they are. Critics'
 * 100-point scores are only used from about 50 up, with 90 "outstanding" much like 4 on Vivino,
 * so they are read from 50: 80 is 3, 90 is 4, 100 is 5.
 */
export function onFiveScale({ score, scale }: SiteScore): number {
  if (scale === 100) return Math.max(0, (score - 50) / 10);
  return (score / scale) * 5;
}

/**
 * One rating from the sites found: their average on the 0–5 scale, rounded to the nearest half
 * point and kept between 1 and 5 like the person's own ratings; undefined without any.
 */
export function calibratedRating(scores: readonly SiteScore[]): number | undefined {
  if (scores.length === 0) return undefined;
  const average = scores.reduce((sum, s) => sum + onFiveScale(s), 0) / scores.length;
  return Math.min(5, Math.max(1, Math.round(average * 2) / 2));
}

/**
 * The online rating to save from the sites found on a day (owner, 2026-10-03): their calibrated
 * rating, with the day and each site's own rating for the wine's notes; undefined without any.
 */
export function onlineRatingFrom(
  sites: readonly SiteRating[],
  day: CalendarDay,
): SavedOnlineRating | undefined {
  const rating = calibratedRating(sites);
  if (rating === undefined) return undefined;
  return {
    checkedOn: { year: day.year, month: day.month },
    rating,
    checkedDay: { year: day.year, month: day.month, day: day.day },
    sites: sites.map(({ site, score, scale }) => ({ site, score, scale })),
  };
}

/** The online rating as it is saved; throws InvalidOnlineRatingError for one that is not valid. */
export function checkedOnlineRating(found: SavedOnlineRating, now: MonthValue): SavedOnlineRating {
  if (!isValidRating(found.rating) || isFutureMonth(found.checkedOn, now)) {
    throw new InvalidOnlineRatingError();
  }
  const day = found.checkedDay;
  if (day !== undefined && (!isRealDay(day) || isFutureMonth(day, now))) {
    throw new InvalidOnlineRatingError();
  }
  const sites = found.sites?.map(siteRating);
  if (sites !== undefined && (sites.length === 0 || sites.length > MAX_SITES)) {
    throw new InvalidOnlineRatingError();
  }
  return {
    checkedOn: { year: found.checkedOn.year, month: found.checkedOn.month },
    rating: found.rating,
    ...(day !== undefined
      ? { checkedDay: { year: day.year, month: day.month, day: day.day } }
      : {}),
    ...(sites !== undefined ? { sites } : {}),
  };
}

function isRealDay({ year, month, day }: CalendarDay): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    Number.isInteger(day) &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function siteRating({ site, score, scale }: SiteRating): SiteRating {
  const name = site.trim().replace(/\s+/g, ' ');
  if (
    name === '' ||
    !SITE_SCALES.includes(scale) ||
    !Number.isFinite(score) ||
    score <= 0 ||
    score > scale
  ) {
    throw new InvalidOnlineRatingError();
  }
  return { site: name, score, scale };
}

/**
 * The wine's notes from its last lookup (owner request, 2026-10-03): one per site, with the day
 * of the lookup. They stay once the person has rated the wine; none without the details.
 */
export function onlineRatingNotes(wine: { onlineRating?: SavedOnlineRating }): OnlineRatingNote[] {
  const found = wine.onlineRating;
  if (!found?.checkedDay || !found.sites) return [];
  const day = found.checkedDay;
  return found.sites.map((site) => ({ day, ...site }));
}

/** The saved online rating to show, or undefined once the wine has a rating of the person's own. */
export function onlineRatingToShow(wine: {
  onlineRating?: SavedOnlineRating;
  tastings: readonly RatedTasting[];
}): SavedOnlineRating | undefined {
  return latestRatedTasting(wine.tastings) ? undefined : wine.onlineRating;
}
